import _flatten from 'lodash/flatten';
import _cloneDeep from 'lodash/cloneDeep';
import _set from 'lodash/set';
import {
    STAxPos,
    STBarGrouping,
    STCrossBetween,
    STGrouping,
    STOrientationDmlC,
    STSchemeColorVal,
    STTickLblPos,
    STTickMark,
    SubChartType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { IBaseSubChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/base-sub-chart.interface';
import { BaseSubChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/base-sub-chart';
import { ChartSeries, SerRules } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2';
import {
    DEFAULT_AXIS_FONT_SIZE,
    DEFAULT_CHART_SERIES_CATEGORIES_PRESET,
    DEFAULT_CHART_VALUES_PRESET,
    DEFAULT_SERIES_NAME,
    DEFAULT_SHAPE_PROPS,
    PresetChartType,
} from '../const';
import { fmtIsDate } from '@tencent/tdocs-format-render';
import {
    IChartAxis,
    IChartLegend,
    IChartPlotArea,
    IChartSpace,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { DeepPartial } from '@src/slide/infrastructure/module/independent/ts-tools/deep-partial';
import ChartDefaultValueFactory from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/default-value-factory';
import { LineJoin } from '@tencent/alloy-renderer';
import { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import { ISubChartData } from '@tencent/slide-sdk-model';
import { ILineCap } from '../../../property/border/border.interface';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

export interface SeriesInitialProps {
    valuesPreset?: Array<Array<number | undefined>>;
    categoriesPreset?: Array<string | undefined>;
    seriesNamesPreset?: Array<string>;
    showDataLabels?: boolean;
}

export interface ModifyPlotAreaProps {
    updateProps?: DeepPartial<Record<keyof IChartPlotArea, any>>;
    removeProps?: DeepPartial<Record<keyof IChartPlotArea, any>>;
}

export interface SeriesPresetPoint {
    value: string;
    category?: string;
}

export abstract class BaseChartPresetHandler {
    protected defaultChartDataPreset = {
        categories: DEFAULT_CHART_SERIES_CATEGORIES_PRESET,
        values: DEFAULT_CHART_VALUES_PRESET,
    };

    protected abstract get ChartSeries(): typeof ChartSeries;
    protected abstract get SubChart(): typeof BaseSubChart;

    public createChartModel(
        type: PresetChartType,
        chartSpace: IChartSpace,
        initialProps?: SeriesInitialProps,
    ): IBaseSubChart {
        // @ts-ignore
        const subChart = new this.SubChart();
        // originType给ui-sdk转换使用，正常在sdk-ui的时候赋值
        // 所以新创建的图表没有originType，这里手动补充一个
        subChart.originType = subChart.type;

        this.setBaseProps(subChart);

        subChart.series = this.generateSeriesPreset(
            this.ChartSeries,
            initialProps?.valuesPreset || this.defaultChartDataPreset.values,
            initialProps?.categoriesPreset || this.defaultChartDataPreset.categories,
            initialProps?.seriesNamesPreset,
            initialProps?.showDataLabels,
            chartSpace?.colorStyle?.id,
        );

        return subChart;
    }

    public modifySubChartType(
        subChartModels: IBaseSubChart[],
        type: PresetChartType,
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined,
        chartSpace: IChartSpace,
    ) {
        const [subChartModel] = subChartModels;

        // 组合图的话，按把所有的系列数据合并
        const oldChartSeries = _flatten(subChartModels.map(subChart => subChart.series));
        const oldSeriesValues = oldChartSeries.map(oldSer => oldSer.dataList.map(point => point.valuePoint?.value));
        // dataList中的categoryPoint是经过处理的，每个series都一样，直接用第一个Ser获取
        const oldSeriesCategories = oldChartSeries[0].dataList.map(point => point.categoryPoint?.value);
        const oldSeriesNames = oldChartSeries.map(ser => ser.name);
        const newSubChart = this.createChartModel(
            type,
            chartSpace,
            // @ts-ignore
            { valuesPreset: oldSeriesValues, categoriesPreset: oldSeriesCategories, seriesNamesPreset: oldSeriesNames },
        );

        // 这里要用老series new一个新series
        oldChartSeries.forEach((oldSer, index) => {
            const newSer = newSubChart.series[index];

            const clonedOldSer = _cloneDeep(oldSer);
            const oldChartPresetHandler = getChartPresetHandlerByChartBaseType(subChartModel.type);
            // 部分场景会存在，某些图表类型会清理他们特有的属性或者数据，这部分属性不会被继承走。
            // 所以这里调用子类的dropSeriesProps去处理即将被继承的ser
            oldChartPresetHandler?.dropSeriesProps({
                // @ts-ignore
                oldSer: clonedOldSer,
                oldSubChart: subChartModel,
                serIndex: index,
            });

            this.extendSeries({
                // @ts-ignore
                newSer,
                // @ts-ignore
                oldSer: clonedOldSer,
                newSubChart,
                oldSubChart: subChartModel,
                newSubChartType: type,
                serIndex: index,
            });
        });

        if (subChartModel.axisIds) {
            newSubChart.axisIds = subChartModel.axisIds;
        }
        if (subChartModel.axesAddIndex !== undefined) {
            newSubChart.axesAddIndex = subChartModel.axesAddIndex;
        }

        newSubChart.dataLabels = subChartModel.dataLabels;

        this.clearDataLabelPosition(newSubChart);

        return newSubChart;
    }

    public modifyPlotArea(param: {
        plotArea: IChartPlotArea;
        newSubChart: IBaseSubChart;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
    }): ModifyPlotAreaProps {
        const { plotArea, newSubChart, targetType, getChartPresetHandlerByChartBaseType } = param;

        const updateProps: ModifyPlotAreaProps['updateProps'] = {};
        const removeProps: ModifyPlotAreaProps['removeProps'] = {};

        this.removeUnrelatedAxis(plotArea, newSubChart, removeProps);

        this.updateValueAxisToCategoryAxis(targetType, plotArea, newSubChart, updateProps, removeProps);

        // 修改图表类型时，老图表对坐标轴的处理
        this.handleChartAxisPropsByOldChartHandler({
            plotArea,
            targetType,
            getChartPresetHandlerByChartBaseType,
            updateProps,
            removeProps,
        });

        this.updateComingAxisProp(targetType, plotArea, newSubChart, updateProps, removeProps);

        // 常用图表都有坐标轴，这里做一个通用的逻辑：当没有坐标轴时，添加坐标轴(如果之前delete应该不影响，这里是plotArea下没有坐标轴属性的时候，添加坐标轴属性，场景未饼图换为柱形图等)
        if (!plotArea.categoryAxes && !plotArea.valueAxes && !plotArea.dateAxes) {
            newSubChart.axisIds = this.generateAxisIds(2);

            return {
                updateProps: {
                    ...this.generateAxesPreset(newSubChart, targetType),
                },
            };
        }

        return {
            updateProps,
            removeProps,
        };
    }

    public getLegendProps(): Partial<IChartLegend> {
        return {};
    }

    // 这里即要subChart也要type的原因是，chartType和createChartType不对等
    public generateAxesPreset(
        subChart: IBaseSubChart,
        type: PresetChartType,
    ): { categoryAxes?: IChartAxis[]; valueAxes?: IChartAxis[]; dateAxes?: IChartAxis[] } {
        const { axisIds } = subChart;
        const [categoryAxisId, valueAxisId] = axisIds || [];
        const grouping = 'grouping' in subChart ? (subChart.grouping as STBarGrouping | STGrouping) : undefined;

        const valueAxes = [
            this.createValueAxis(
                {
                    axId: valueAxisId,
                    crossAx: categoryAxisId,
                },
                type,
                grouping,
            ),
        ];

        const isCategoryDateTypeFormatCode = fmtIsDate(subChart.series[0].categoryFormatCode || '');

        // 如果是日期格式的formatCode，就要创建axis类型坐标轴
        if (isCategoryDateTypeFormatCode && !this.needCategoryAxis()) {
            const dateAxes = [
                this.createDateAxis(
                    {
                        axId: categoryAxisId,
                        crossAx: valueAxisId,
                    },
                    type,
                ),
            ];

            return {
                dateAxes,
                valueAxes,
            };
        }

        const categoryAxes = [
            this.createCategoryAxis(
                {
                    axId: categoryAxisId,
                    crossAx: valueAxisId,
                },
                type,
            ),
        ];

        return {
            categoryAxes,
            valueAxes,
        };
    }

    // 批量对坐标轴属性做处理。场景为部分图表，在切换时，需要重置/修改自身的一些属性
    // 如条形图会自行转换坐标轴位置，在切换时，需要将坐标轴位置还原
    protected handleChartAxisPropsByOldChartHandler(param: {
        plotArea: IChartPlotArea;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
        updateProps: DeepPartial<Record<keyof IChartPlotArea, any>>;
        removeProps: DeepPartial<Record<keyof IChartPlotArea, any>>;
    }) {
        const { plotArea, getChartPresetHandlerByChartBaseType } = param;
        plotArea.charts.forEach((subChart) => {
            const oldChartPresetHandler = getChartPresetHandlerByChartBaseType(subChart.type);

            oldChartPresetHandler?.handleAxisPropsOnModify({
                ...param,
                subChart,
            });
        });
    }

    // 当自身类型变更时，对坐标轴属性的处理
    protected handleAxisPropsOnModify(param: {
        subChart: IBaseSubChart;
        plotArea: IChartPlotArea;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
        updateProps: DeepPartial<Record<keyof IChartPlotArea, any>>;
        removeProps: DeepPartial<Record<keyof IChartPlotArea, any>>;
    }) {
        // 基类什么也不做
    }

    protected dropSeriesProps({
        oldSer,
        serIndex,
        oldSubChart,
    }: {
        oldSer: ChartSeries;
        serIndex: number;
        oldSubChart: IBaseSubChart;
    }): ChartSeries {
        return oldSer;
    }

    protected extendSeries({
        newSer,
        oldSer,
        newSubChart,
        oldSubChart,
        newSubChartType,
        serIndex,
    }: {
        newSer: ChartSeries;
        oldSer: ChartSeries;
        newSubChart: IBaseSubChart;
        oldSubChart: IBaseSubChart;
        newSubChartType: PresetChartType;
        serIndex: number;
    }) {
        // 拷贝一份，防止后续修改自身的DataList修改到原本model
        // 原本的clone提到了外层
        newSer.dataList = oldSer.dataList.map((dataItem, index) => {
            const newDataPoint = newSer.dataList[index].dataPoint;
            if (newDataPoint && newSubChart.varyColors) {
                dataItem.dataPoint = newDataPoint;
            }
            if (oldSubChart.varyColors && !newSubChart.varyColors) {
                delete dataItem.dataPoint;
            }
            return dataItem;
        });

        if (oldSer.name) {
            newSer.name = oldSer.name;
        }

        newSer.categoryType = oldSer.categoryType;
        newSer.categoryFormatCode = oldSer.categoryFormatCode;
        newSer.valueType = oldSer.valueType;
        newSer.valueFormatCode = oldSer.valueFormatCode;
        newSer.dataLabels = oldSer.dataLabels;

        if (oldSer.trendlines && this.shouldExtendTrendline(newSubChartType)) {
            newSer.trendlines = oldSer.trendlines;
        }

        newSer.props = {
            ...oldSer.props,
            style: this.getStyle(newSer, oldSer, newSubChart, oldSubChart),
        };
    }

    protected getStyle(
        newSer: ChartSeries, oldSer: ChartSeries,
        newSubChart: IBaseSubChart, oldSubChart: IBaseSubChart,
    ) {
        const newStyleBorder = newSer.props?.style?.border;
        const oldStyleBorder = oldSer.props?.style?.border;
        const style = {
            ...oldSer.props?.style,
            ...(newStyleBorder && oldStyleBorder
                ? {
                    // props的合并有点讲究，可能需要到实际的key，比如border会合并,这里在有border才去生成合并border这个key
                    border: {
                        ...newStyleBorder,
                        ...oldStyleBorder,
                    },
                }
                : {}),
        };
        return style;
    }

    protected shouldExtendTrendline(targetType: PresetChartType) {
        return false;
    }

    protected setBaseProps(subChart: IBaseSubChart) {
        subChart.varyColors = false;
        // 默认两根坐标轴
        subChart.axisIds = this.generateAxisIds(2);
    }

    // TODO 这里要改，应该面向uiModel而不是sdkData
    protected generatePresetSeriesInitialProps(
        seriesIndex: number,
        points: Array<SeriesPresetPoint>,
        seriesName?: string,
        showDataLabels = false,
    ) {
        return {
            idx: seriesIndex,
            order: seriesIndex,
            seriesText: {
                value: seriesName ?? `${DEFAULT_SERIES_NAME} ${seriesIndex + 1}`,
            },
            values: { formatCode: 'General', type: 'nr' },
            categoryAxisData: { type: 'sr' },
            valueCount: points.length,
            valueFormatCode: 'General',
            props: {
                // 不给series颜色，让office自行继承主题色，前端也走自行逆向获取主题色的逻辑
                // background: seriesThemeColors[seriesIndex],
            },
            categories: points.map(point => ({
                categoryPoint: { value: point.category },
                valuePoint: { value: point.value },
            })),
            dLbls: showDataLabels ? { showVal: true } : false,
        };
    }

    protected generateSeriesInitialOptions(categoryPointList: Array<string | undefined>): SerRules {
        return {
            // @ts-ignore
            categoryPointList: categoryPointList.map((name) => {
                if (name !== undefined) {
                    return {
                        value: name,
                    };
                }

                return undefined;
            }),
            count: categoryPointList.length,
        };
    }

    protected generateSeriesPreset(
        ChartSeriesConstructor: typeof ChartSeries,
        valuesPreset: SeriesInitialProps['valuesPreset'] = DEFAULT_CHART_VALUES_PRESET,
        categoriesPreset: SeriesInitialProps['categoriesPreset'] = DEFAULT_CHART_SERIES_CATEGORIES_PRESET,
        seriesNamesPreset: SeriesInitialProps['seriesNamesPreset'],
        showDataLabels?: boolean,
        colorPaletteId?: string,
    ) {
        const barChartSeriesPointsPreset: SeriesPresetPoint[][] = valuesPreset
            .map((valuesPreset, index) => valuesPreset.map((value): SeriesPresetPoint => ({
                value: String(value),
                category: String(categoriesPreset[index]),
            })));

        const presetSeries = barChartSeriesPointsPreset.map((seriesPoints, seriesIndex) => {
            const baseSeriesInitialProps = this
                .generatePresetSeriesInitialProps(
                    seriesIndex,
                    seriesPoints,
                    seriesNamesPreset?.[seriesIndex],
                    showDataLabels,
                );
            const chartSeries = new ChartSeriesConstructor(
                baseSeriesInitialProps,
                this.generateSeriesInitialOptions(categoriesPreset),
            );

            return chartSeries;
        });

        return presetSeries;
    }

    protected createCategoryAxis(props: Partial<IChartAxis>, type: PresetChartType): IChartAxis {
        return this.createAxis({
            position: STAxPos.b,
            props: {
                style: {
                    ...DEFAULT_SHAPE_PROPS.style,
                    border: {
                        linecap: ILineCap.Butt,
                        linejoin: LineJoin.Round,
                        width: 1.5,
                        color: '#D9D9D9',
                        tdColor: new TDColor({
                            rgbaStr: '#D9D9D9',
                        }),
                    },
                },
            },
            ...props,
        });
    }

    protected createValueAxis(
        props: Partial<IChartAxis>,
        type: PresetChartType,
        grouping?: STBarGrouping | STGrouping,
    ): IChartAxis {
        let numFmtProps;
        if (grouping === STBarGrouping.percentStacked || grouping === STGrouping.percentStacked) {
            numFmtProps = {
                numFmt: {
                    formatCode: '0%',
                    // @ts-ignore
                    sourceLinked: false,
                },
            };
        }
        return this.createAxis({
            position: STAxPos.l,
            crossBetween: STCrossBetween.between,
            majorGridlines: {
                props: {
                    style: {
                        ...DEFAULT_SHAPE_PROPS.style,
                        border: {
                            linecap: ILineCap.Butt,
                            linejoin: LineJoin.Round,
                            width: 1.5,
                            color: '#D9D9D9',
                            tdColor: new TDColor({
                                rgbaStr: '#D9D9D9',
                            }),
                        },
                    },
                },
            },
            ...numFmtProps,
            ...props,
        });
    }

    protected createDateAxis(props: Partial<IChartAxis>, type: PresetChartType) {
        return this.createAxis({
            position: STAxPos.b,
            // @ts-ignore
            numFmt: { formatCode: 'm/d/yy', sourceLinked: 0 },
            ...props,
        });
    }

    protected createAxis(props?: Partial<IChartAxis>): IChartAxis {
        return {
            isDelete: false,
            position: STAxPos.l,
            props: DEFAULT_SHAPE_PROPS,
            paragraphList: ChartDefaultValueFactory.createDefaultParagraph({ fontSize: DEFAULT_AXIS_FONT_SIZE }),
            scaling: {
                orientation: STOrientationDmlC.minMax,
            },
            numFmt: {
                formatCode: 'General',
                // @ts-ignore
                sourceLinked: 0,
            },
            majorTickMark: STTickMark.none,
            minorTickMark: STTickMark.none,
            tickLabelPosition: STTickLblPos.nextTo,
            ...props,
        };
    }

    protected getCategoryAxisPosition(targetType: PresetChartType): STAxPos {
        return STAxPos.b;
    }

    protected getValueAxisPosition(targetType: PresetChartType): STAxPos {
        return STAxPos.l;
    }

    protected getDateAxisPosition(targetType: PresetChartType): STAxPos {
        return STAxPos.b;
    }

    protected needCategoryAxis() {
        return false;
    }

    protected getValueAxisCrossBetween(targetType: PresetChartType): STCrossBetween {
        return STCrossBetween.between;
    }

    protected getDataItemSdkData(subChartModel: ISubChartModel, serIndex: number, dataItemIndex: number) {
        const index = Number(subChartModel.id);
        return (((subChartModel as any).context?.getChartModel())?.sdkData?.props
            .chartSpace?.chart?.plotArea?.charts?.[index].data as ISubChartData)
            .ser?.[serIndex]?.categories?.[dataItemIndex];
    }

    protected revertUIBorderColorToSchemeClr(
        oldSubChart: ISubChartModel,
        dataItem,
        serIndex: number,
        dataItemIndex: number,
    ) {
        const sdkPoint = this.getDataItemSdkData(oldSubChart, serIndex, dataItemIndex);
        const sdkLnProps = sdkPoint?.dataPoint?.spPr?.ln;
        const schemeClrVal = sdkLnProps?.egLineFillProperties?.solidFill?.egColorChoice?.schemeClr?.val;
        if (schemeClrVal) {
            dataItem.dataPoint.props.style.border.color = {
                colorValue: STSchemeColorVal[schemeClrVal],
                colorType: 'scheme',
            };
        }

        return dataItem;
    }

    protected removeDataPointUIDataSchemeClr(
        uiData: any,
        subChart: ISubChartModel,
        serIndex: number,
        pointIndex: number,
    ) {
        const dataPointSdkData = this.getDataItemSdkData(
            subChart,
            serIndex,
            pointIndex,
        )?.dataPoint;
        if (dataPointSdkData?.spPr?.ln?.egLineFillProperties?.solidFill?.egColorChoice?.schemeClr?.val
            === STSchemeColorVal.lt1
        ) {
            // 如果是自动填充的边框颜色（如拼图默认会有个白色边框），会导致折线图显示为白色，office这里把这个border去掉了
            delete uiData?.props?.style?.border;
        }
    }

    // TOOD 后续调整算法
    private generateAxisIds(count: number): number[] {
        // axisId在ooxml里是一个int32，这里用日期生成一个在int32范围内的不会重复的baseNumber
        const baseNumber = Number(`${Date.now()}`.slice(-8));

        return new Array(count).fill(baseNumber)
            .map((id, index) => id + index);
    }

    private clearDataLabelPosition(subChart?: IBaseSubChart) {
        if (subChart?.dataLabels) {
            delete subChart.dataLabels.labelPosition;
        }

        subChart?.series.forEach((ser) => {
            if (ser.dataLabels) {
                delete ser.dataLabels.labelPosition;
            }

            ser.dataList?.map((point) => {
                if (point.dataLabel) {
                    delete point.dataLabel.labelPosition;
                }
            });
        });
    }

    private removeUnrelatedAxis(plotArea: IChartPlotArea, newSubChart: IBaseSubChart, removeProps: any) {
        const { dateAxes, valueAxes, categoryAxes } = plotArea;
        const relativeAxisIds = newSubChart.axisIds;

        [
            { axes: valueAxes, key: 'valueAxes' },
            { axes: categoryAxes, key: 'categoryAxes' },
            { axes: dateAxes, key: 'dateAxes' },
        ].forEach(({ axes, key }) => {
            axes?.forEach((axis, index) => {
                if (!relativeAxisIds?.includes(axis.axId!)) {
                    if (!removeProps[key]) {
                        removeProps[key] = [];
                    }

                    removeProps[key][index] = null;
                }
            });
        });
    }

    private updateValueAxisToCategoryAxis(
        targetType: PresetChartType,
        plotArea: IChartPlotArea,
        newSubChart: IBaseSubChart,
        updateProps: DeepPartial<Record<keyof IChartPlotArea, any>>,
        removeProps: DeepPartial<Record<keyof IChartPlotArea, any>>,
    ) {
        // 如果subChart关联的两个坐标轴都是valueAxis，就把"类别"关联的坐标轴改成categoryAxis
        // 因为现在还不支持 修改成散点图和气泡图，但是支持这两种转成其他图表，其他图表不存在双值坐标轴

        const { axisIds } = newSubChart;
        const { valueAxes, categoryAxes } = plotArea;

        const relativeValuesAxis = valueAxes?.filter(axis => axisIds?.includes(axis.axId!));
        if (relativeValuesAxis && relativeValuesAxis.length > 1) {
            valueAxes?.forEach((axis, index) => {
                // 两个value axis时，下面的坐标轴转换为categoryAxis
                if (axis.position === STAxPos.b) {
                    const categoryRelativeAxis = _cloneDeep(axis);
                    // @ts-ignore
                    categoryRelativeAxis.numFmt = { formatCode: 'General', sourceLinked: 0 };
                    categoryRelativeAxis.crossBetween = undefined;

                    // 当valueAxis转换成categoryAxis的时候，需要跟office一样，移除min max的设置。
                    if (categoryRelativeAxis.scaling) {
                        delete categoryRelativeAxis.scaling.max;
                        delete categoryRelativeAxis.scaling.min;
                    }

                    // 调整位置
                    categoryRelativeAxis.position = this.getCategoryAxisPosition(targetType);

                    if (!removeProps.valueAxes) {
                        removeProps.valueAxes = [];
                    }
                    removeProps!.valueAxes[index] = null;

                    if (!updateProps.categoryAxes) {
                        updateProps.categoryAxes = [];
                    }
                    updateProps.categoryAxes[categoryAxes?.length || 0] = categoryRelativeAxis;
                } else {
                    if (!updateProps.valueAxes) {
                        updateProps.valueAxes = [];
                    }
                    // 双值的midcat要更新为默认值
                    updateProps.valueAxes[0] = {
                        crossBetween: null,
                    };
                }
            });
        }
    }

    // 切换为新图表时，新图表对到来的axisProps进行统一处理
    private updateComingAxisProp(
        targetType: PresetChartType,
        plotArea: IChartPlotArea,
        newSubChart: IBaseSubChart,
        updateProps: DeepPartial<Record<keyof IChartPlotArea, any>>,
        removeProps: DeepPartial<Record<keyof IChartPlotArea, any>>,
    ) {
        // 这里统一处理下，写在各个子类逻辑重复
        const isTargetTypePercentStack = this.isPercentStackType(targetType);
        if (isTargetTypePercentStack) {
            _set(updateProps, 'valueAxes[0].numFmt', {
                formatCode: '0%',
                // @ts-ignore
                sourceLinked: 0,
            });
            return;
        }

        // 当前是百分比堆积图表，且目标图表不是堆积，就取消0%的formatCode
        const isCurrentTypePercentStack = plotArea.valueAxes?.find(axis => axis.numFmt?.formatCode === '0%')
            && !!plotArea.charts.find(subChart => 'grouping' in subChart
                    && (subChart.grouping === STGrouping.percentStacked || STBarGrouping.percentStacked));
        if (isCurrentTypePercentStack) {
            _set(updateProps, 'valueAxes[0].numFmt', {
                formatCode: 'General',
                // @ts-ignore
                sourceLinked: 0,
            });
        }

        const { valueAxes } = plotArea;
        const valueAxis = valueAxes?.[0];
        // 更新valueAxis crossBetween字段
        if (valueAxis && valueAxis.crossBetween !== this.getValueAxisCrossBetween(targetType)) {
            _set(updateProps, 'valueAxes[0].crossBetween', this.getValueAxisCrossBetween(targetType));
        }
    }

    private isPercentStackType(type: PresetChartType) {
        return (
            type === PresetChartType.BAR_PERCENT_STACKED
            || type === PresetChartType.COLUMN_PERCENT_STACKED
            || type === PresetChartType.LINE_PERCENT_STACKED
            || type === PresetChartType.LINE_PERCENT_STACKED_WITH_POINT
            || type === PresetChartType.AREA_PERCENT_STACKED
        );
    }
}
